import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, HelpCircle, RefreshCw, RotateCcw, Send, ThumbsDown, ThumbsUp } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Loader, Pill } from "@/components/Bits";
import { Markdown } from "@/components/Markdown";

const classTone = { in_topic: "green", adjacent: "amber", off_topic: "red" };
const classLabel = { in_topic: "on topic", adjacent: "nearby topic", off_topic: "beyond this topic" };

export const CardDeck = ({ topicId }) => {
  const [deck, setDeck] = useState(null);
  const [index, setIndex] = useState(0);
  const [variants, setVariants] = useState({});
  const [question, setQuestion] = useState("");
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get(`/topics/${topicId}/cards`);
    setDeck(data);
    return data;
  }, [topicId]);

  useEffect(() => {
    setIndex(0);
    setVariants({});
    setAnswers({});
    load().catch((e) => toast.error(errMsg(e)));
  }, [load]);

  const pending = deck && !["ready", "partial"].includes(deck.status);
  const streaming = deck?.status === "partial";

  useEffect(() => {
    if (!pending && !streaming) return;
    const id = setInterval(() => load(), streaming ? 4000 : 8000);
    return () => clearInterval(id);
  }, [pending, streaming, load]);

  if (!deck) return <Loader label="Opening your cards…" />;
  if (deck.status === "gathering_sources")
    return <Loader label="Research agent is gathering real sources for this topic…" note="A few seconds — then your first card lands." />;
  if (pending)
    return <Loader label="Teacher agent is cutting this topic into cards…" note="One idea per card, in the order that makes it click." />;

  const cards = deck.cards || [];
  const expected = Math.max(deck.total_expected || cards.length, cards.length);
  const plan = deck.plan || [];
  const card = cards[Math.min(index, cards.length - 1)];
  const state = deck.cards_state?.[card.card_id] || {};
  const cardVariants = variants[card.card_id] || [];
  const answer = answers[card.card_id];
  const understoodCount = cards.filter((c) => deck.cards_state?.[c.card_id]?.understood).length;

  const act = async (path, body) => {
    const { data } = await api.post(`/topics/${topicId}/cards/${card.card_id}/${path}`, body);
    return data;
  };

  const understood = async () => {
    setBusy("up");
    try {
      const data = await act("understood");
      await load();
      if (data.deck_complete) toast.success("Deck complete — ready for the topic assessment");
      setIndex((i) => Math.min(i + 1, cards.length - 1));
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy("");
    }
  };

  const explainAgain = async () => {
    setBusy("down");
    try {
      const data = await act("explain-again");
      if (data.capped) {
        toast.error(data.message);
      } else {
        setVariants({ ...variants, [card.card_id]: [...cardVariants, data.body] });
        if (data.escalated) toast.error("Third try — your guardian has been told so a human can step in.");
      }
      await load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy("");
    }
  };

  const ask = async () => {
    if (!question.trim()) return;
    setBusy("ask");
    try {
      const data = await act("ask", { question });
      setAnswers({ ...answers, [card.card_id]: { ...data, question } });
      setQuestion("");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy("");
    }
  };

  return (
    <div data-testid="card-deck">
      <div className="flex flex-wrap items-center gap-3">
        <Pill tone="blue" testId="card-counter">
          Card {index + 1} of {expected}
        </Pill>
        <Pill tone="green">{understoodCount} understood</Pill>
        {streaming && (
          <Pill tone="amber" testId="deck-streaming">
            writing the rest ({cards.length}/{expected})
          </Pill>
        )}
        {state.needs_human && <Pill tone="red">flagged for a human</Pill>}
        <button data-testid="card-refresh-deck" onClick={() => api.get(`/topics/${topicId}/cards?refresh=true`).then(load)} className="ml-auto inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-900">
          <RefreshCw className="w-3.5 h-3.5" /> New deck
        </button>
      </div>

      <div className="mt-4 flex gap-1.5">
        {Array.from({ length: expected }).map((_, i) => {
          const written = cards[i];
          return (
            <button
              key={written?.card_id || `slot-${i}`}
              onClick={() => written && setIndex(i)}
              disabled={!written}
              data-testid={`card-rail-${i}`}
              className={`h-1.5 flex-1 rounded-full transition-colors ${
                written && deck.cards_state?.[written.card_id]?.understood
                  ? "bg-emerald-500"
                  : i === index
                  ? "bg-slate-900"
                  : written
                  ? "bg-slate-200"
                  : "bg-slate-100"
              }`}
              title={written?.title || plan[i]?.title || "writing…"}
            />
          );
        })}
      </div>

      <div key={card.card_id} className="mt-6 card-flat p-7 reveal" data-testid={`card-${card.card_id}`}>
        <h3 className="font-display text-2xl font-bold" data-testid="card-title">{card.title}</h3>
        <div className="mt-4 prose-read text-[1.02rem]">
          <Markdown text={card.body} />
        </div>
        {card.example && (
          <div className="mt-5 border-l-2 border-amber-400 pl-4">
            <p className="rule-label">Worked example</p>
            <div className="mt-2 prose-read text-[0.98rem]">
              <Markdown text={card.example} />
            </div>
          </div>
        )}
        {card.takeaway && (
          <p className="mt-5 text-sm font-semibold text-slate-800 bg-slate-50 border border-slate-200 rounded-xl p-4">{card.takeaway}</p>
        )}

        {cardVariants.map((v, i) => (
          <div key={i} className="mt-5 border border-blue-200 bg-blue-50/60 rounded-xl p-5" data-testid={`card-variant-${i}`}>
            <div className="flex items-center gap-2">
              <RotateCcw className="w-3.5 h-3.5 text-blue-700" />
              <p className="rule-label">Another way — attempt {i + 2}</p>
            </div>
            <div className="mt-2 prose-read text-[0.98rem]">
              <Markdown text={v} />
            </div>
          </div>
        ))}

        {answer && (
          <div className="mt-5 border border-slate-200 rounded-xl p-5" data-testid="card-answer">
            <div className="flex flex-wrap items-center gap-2">
              <Pill tone={classTone[answer.classification]}>{classLabel[answer.classification]}</Pill>
              <span className="text-xs text-slate-500">you asked: {answer.question}</span>
            </div>
            {answer.answer && (
              <div className="mt-3 prose-read text-[0.98rem]">
                <Markdown text={answer.answer} />
              </div>
            )}
            <p className="mt-3 text-sm font-medium text-slate-700">{answer.redirect}</p>
          </div>
        )}

        <div className="mt-7 flex flex-wrap gap-2">
          <button data-testid="card-understood" onClick={understood} disabled={!!busy} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 transition-colors">
            <ThumbsUp className="w-4 h-4" /> {state.understood ? "Understood" : "Got it"}
          </button>
          <button data-testid="card-explain-again" onClick={explainAgain} disabled={!!busy} className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-slate-300 text-sm font-semibold hover:border-slate-500 disabled:opacity-50 transition-colors">
            <ThumbsDown className="w-4 h-4" /> Explain again
            {state.explain_attempts ? ` (${deck.max_explain_attempts - state.explain_attempts} left)` : ""}
          </button>
          {index > 0 && (
            <button data-testid="card-prev" onClick={() => setIndex(index - 1)} className="px-5 py-3 rounded-full text-sm font-semibold text-slate-600 hover:bg-slate-100 transition-colors">
              Previous
            </button>
          )}
          {index === cards.length - 1 && streaming && (
            <span className="px-5 py-3 text-sm font-medium text-amber-700" data-testid="card-writing-next">
              Writing the next card…
            </span>
          )}
          {index < cards.length - 1 && (
            <button data-testid="card-next" onClick={() => setIndex(index + 1)} className="px-5 py-3 rounded-full text-sm font-semibold text-slate-600 hover:bg-slate-100 transition-colors">
              Skip ahead
            </button>
          )}
        </div>

        <div className="mt-6 border-t border-slate-200 pt-5">
          <div className="flex items-center gap-2">
            <HelpCircle className="w-4 h-4 text-blue-600" />
            <p className="rule-label">Ask about this card</p>
          </div>
          <div className="mt-3 flex gap-2">
            <input
              data-testid="card-question"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && ask()}
              placeholder="What don't you get yet?"
              className="flex-1 px-4 py-2.5 rounded-xl border border-slate-300 text-sm focus:outline-none focus:border-blue-500"
            />
            <button data-testid="card-ask" onClick={ask} disabled={busy === "ask"} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-50 transition-colors">
              <Send className="w-4 h-4" /> {busy === "ask" ? "Thinking…" : "Ask"}
            </button>
          </div>
        </div>
      </div>

      {!streaming && understoodCount === cards.length && cards.length > 0 && (
        <p className="mt-5 inline-flex items-center gap-2 text-sm text-emerald-700 font-semibold" data-testid="deck-complete">
          <CheckCircle2 className="w-4 h-4" /> Every card understood — take the topic assessment when you're ready.
        </p>
      )}
    </div>
  );
};
